import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

/* ─── Types ──────────────────────────────────────────────────────────── */

export interface ProjectBrief {
  id?: string;
  project_id: string;
  client_goal: string;
  target_outcome: string;
  project_background: string;
  must_haves: string;
  nice_to_haves: string;
  design_preferences: string;
  budget_notes: string;
  timeline_notes: string;
  priority_notes: string;
  success_definition: string;
  concern_points: string;
  additional_requests: string;
  created_by?: string | null;
  updated_by?: string | null;
}

const EMPTY_BRIEF = (projectId: string): ProjectBrief => ({
  project_id: projectId,
  client_goal: '',
  target_outcome: '',
  project_background: '',
  must_haves: '',
  nice_to_haves: '',
  design_preferences: '',
  budget_notes: '',
  timeline_notes: '',
  priority_notes: '',
  success_definition: '',
  concern_points: '',
  additional_requests: '',
});

/** Field definition with mapping to the DB column key */
interface IntentField {
  key: keyof ProjectBrief;
  label: string;
  clientLabel: string;
  placeholder: string;
}

/** Field sets by project type */
const FIELD_SETS: Record<string, IntentField[]> = {
  Renovation: [
    { key: 'project_background', label: 'Current Problem', clientLabel: 'What Needs Fixing', placeholder: 'Describe the current problem or reason for renovation' },
    { key: 'client_goal', label: 'Lifestyle Goal', clientLabel: 'Your Lifestyle Goal', placeholder: 'How do you want your life to improve after this project?' },
    { key: 'must_haves', label: 'Must-Haves', clientLabel: 'Must-Haves', placeholder: 'Non-negotiable requirements for this renovation' },
    { key: 'nice_to_haves', label: 'Nice-to-Haves', clientLabel: 'Nice-to-Haves', placeholder: 'Optional extras if budget and time allow' },
    { key: 'budget_notes', label: 'Budget Priority', clientLabel: 'Budget & Priority', placeholder: 'Budget range and what matters most — quality, speed, or cost?' },
    { key: 'success_definition', label: 'Success Criteria', clientLabel: 'What Success Looks Like', placeholder: 'How will you know this renovation was delivered well?' },
  ],
  Development: [
    { key: 'client_goal', label: 'Investment Purpose', clientLabel: 'Investment Purpose', placeholder: 'Why is this project being built — to sell, rent, or hold?' },
    { key: 'target_outcome', label: 'Target Buyer / Tenant', clientLabel: 'Target Buyer or Tenant', placeholder: 'Who is the ideal buyer or tenant for this property?' },
    { key: 'success_definition', label: 'Target Outcome', clientLabel: 'Target Outcome', placeholder: 'What financial or business outcome does this project need to achieve?' },
    { key: 'budget_notes', label: 'Budget / Return Priority', clientLabel: 'Budget & Return Priority', placeholder: 'Budget targets and expected return — e.g. build under $X, sell for $Y' },
    { key: 'concern_points', label: 'Risk Concerns', clientLabel: 'Concerns & Risks', placeholder: 'Any market, timeline, or build risks you are monitoring?' },
    { key: 'priority_notes', label: 'Success Criteria', clientLabel: 'How You Will Measure Success', placeholder: 'What metrics define success — timeline, budget, quality, sale price?' },
  ],
  Unknown: [
    { key: 'client_goal', label: 'Project Purpose', clientLabel: 'Project Purpose', placeholder: 'What is the main purpose of this project?' },
    { key: 'project_background', label: 'Current Challenge', clientLabel: 'Current Situation', placeholder: 'What challenge or opportunity led to this project?' },
    { key: 'target_outcome', label: 'Target Outcome', clientLabel: 'Target Outcome', placeholder: 'What does the finished project need to deliver?' },
    { key: 'must_haves', label: 'Must-Haves', clientLabel: 'Must-Haves', placeholder: 'Non-negotiable requirements' },
    { key: 'design_preferences', label: 'Preferences', clientLabel: 'Preferences', placeholder: 'Style, material, or design preferences' },
    { key: 'success_definition', label: 'Success Criteria', clientLabel: 'Success Criteria', placeholder: 'How will you know this project succeeded?' },
  ],
};

function getFieldsForType(projectType?: string | null): IntentField[] {
  if (projectType === 'Renovation') return FIELD_SETS.Renovation;
  if (projectType === 'Development') return FIELD_SETS.Development;
  return FIELD_SETS.Unknown;
}

/* ─── Props ──────────────────────────────────────────────────────────── */

interface Props {
  projectId: string;
  userId: string;
  projectType?: string | null;
  mode: 'edit' | 'readonly';
  labelStyle?: 'client' | 'internal';
}

/* ─── Component ──────────────────────────────────────────────────────── */

export default function ProjectIntentSection({
  projectId,
  userId,
  projectType,
  mode,
  labelStyle = 'internal',
}: Props) {
  const [brief, setBrief] = useState<ProjectBrief>(EMPTY_BRIEF(projectId));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<ProjectBrief>(EMPTY_BRIEF(projectId));
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  const fields = getFieldsForType(projectType);

  const fetchBrief = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('jphph_project_briefs')
      .select('*')
      .eq('project_id', projectId)
      .maybeSingle();
    if (data) {
      const b = data as ProjectBrief;
      setBrief(b);
      setDraft(b);
    } else {
      const empty = EMPTY_BRIEF(projectId);
      setBrief(empty);
      setDraft(empty);
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    fetchBrief();
  }, [fetchBrief]);

  const handleSave = async () => {
    setSaving(true);
    const payload: ProjectBrief = {
      ...draft,
      updated_by: userId,
      created_by: brief.created_by || userId,
    };
    const { error } = await supabase
      .from('jphph_project_briefs')
      .upsert(payload, { onConflict: 'project_id' });
    setSaving(false);
    if (!error) {
      setBrief(payload);
      setIsEditing(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  const handleCancel = () => {
    setDraft(brief);
    setIsEditing(false);
    setExpandedKeys(new Set());
  };

  const toggleExpand = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const sectionTitle = labelStyle === 'client' ? 'Project Intent' : 'Project Intent';
  const sectionSubtitle = labelStyle === 'client'
    ? 'Original client brief, project purpose, and success criteria'
    : 'Original client brief, project purpose, and success criteria';

  const filledFields = fields.filter((f) => !!brief[f.key]?.trim());
  const hasContent = filledFields.length > 0;

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="h-4 bg-gray-100 rounded w-40 mb-2 animate-pulse"></div>
        <div className="h-3 bg-gray-100 rounded w-56 mb-4 animate-pulse"></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-[#FEF0E7] flex items-center justify-center">
            <i className="ri-bubble-chart-line text-[#E8621A] text-sm"></i>
          </div>
          <div>
            <h3 className="font-bold text-[#1A1A1A] text-sm">{sectionTitle}</h3>
            <p className="text-[#9CA3AF] text-xs mt-0.5">{sectionSubtitle}</p>
          </div>
        </div>
        {mode === 'edit' && !isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-1.5 text-[#E8621A] text-xs font-semibold cursor-pointer whitespace-nowrap"
          >
            <i className="ri-edit-line text-sm"></i>
            {hasContent ? 'Edit' : 'Add'}
          </button>
        )}
      </div>

      {/* Saved toast */}
      {saved && (
        <div className="mx-5 mt-3 bg-[#F0FDF4] border border-[#BBF7D0] rounded-xl px-3 py-2 flex items-center gap-2">
          <i className="ri-check-line text-[#059669] text-sm"></i>
          <span className="text-[#059669] text-xs font-semibold">Saved successfully</span>
        </div>
      )}

      <div className="p-5">
        {/* ── Edit mode ── */}
        {mode === 'edit' && isEditing ? (
          <div className="space-y-4">
            {fields.map((field) => {
              const label = labelStyle === 'client' ? field.clientLabel : field.label;
              return (
                <div key={field.key}>
                  <label className="block text-xs font-semibold text-[#374151] mb-1.5">
                    {label}
                  </label>
                  <textarea
                    value={(draft[field.key] as string) || ''}
                    onChange={(e) =>
                      setDraft((prev) => ({ ...prev, [field.key]: e.target.value }))
                    }
                    placeholder={field.placeholder}
                    rows={2}
                    maxLength={500}
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#374151] placeholder-gray-300 focus:outline-none focus:border-[#E8621A] transition-colors resize-none"
                  />
                </div>
              );
            })}
            <div className="flex gap-2 pt-2">
              <button
                onClick={handleCancel}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-[#6B7280] text-sm font-semibold cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-2.5 rounded-xl bg-[#E8621A] text-white text-sm font-bold cursor-pointer disabled:opacity-50 whitespace-nowrap"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          /* ── Read mode ── */
          !hasContent ? (
            <div className="text-center py-6">
              <div className="w-12 h-12 rounded-full bg-[#F9FAFB] flex items-center justify-center mx-auto mb-3">
                <i className="ri-bubble-chart-line text-2xl text-[#D1D5DB]"></i>
              </div>
              <p className="text-[#374151] font-semibold text-sm mb-1">
                No project intent recorded yet
              </p>
              <p className="text-[#9CA3AF] text-xs max-w-xs mx-auto leading-relaxed">
                Add the client brief to clarify the project purpose and success criteria.
              </p>
              {mode === 'edit' && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="mt-3 px-4 py-2 rounded-xl bg-[#E8621A] text-white text-xs font-bold cursor-pointer whitespace-nowrap"
                >
                  Add Project Intent
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {filledFields.map((field) => {
                const label = labelStyle === 'client' ? field.clientLabel : field.label;
                const value = brief[field.key] as string;
                const isExpanded = expandedKeys.has(field.key as string);
                const needsExpand = value.length > 120;

                return (
                  <div
                    key={field.key}
                    className="rounded-xl border border-gray-100 bg-[#FAFAF9] p-3.5 hover:border-gray-200 transition-colors"
                  >
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <div className="w-1 h-3.5 rounded-full bg-[#E8621A] flex-shrink-0"></div>
                      <span className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">
                        {label}
                      </span>
                    </div>
                    <p
                      className={`text-[#374151] text-sm leading-relaxed whitespace-pre-wrap ${
                        !isExpanded && needsExpand ? 'line-clamp-2' : ''
                      }`}
                    >
                      {value}
                    </p>
                    {needsExpand && (
                      <button
                        onClick={() => toggleExpand(field.key as string)}
                        className="mt-1.5 text-[#E8621A] text-xs font-semibold cursor-pointer whitespace-nowrap"
                      >
                        {isExpanded ? (
                          <span className="flex items-center gap-1">
                            <i className="ri-arrow-up-s-line"></i>Show less
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <i className="ri-arrow-down-s-line"></i>Show more
                          </span>
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>
    </div>
  );
}